import React, { useState } from 'react';
import PRODUCTION_CONFIG from '../config/production.js';

const CampaignTrackingQuickActionModal = ({ campaign, currentUser, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    action_type: 'observation_added',
    description: '',
    user_name: currentUser?.name || currentUser?.email || 'Usuario'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const actionTypes = [
    { value: 'observation_added', label: '👁️ Observación', color: 'bg-purple-100 text-purple-800' },
    { value: 'note_added', label: '📝 Nota', color: 'bg-yellow-100 text-yellow-800' }
  ];

  const quickTemplates = {
    observation_added: [
      'La campaña está mostrando buenos resultados iniciales',
      'Se detectó una mejora en el CTR después de los ajustes',
      'Las conversiones han aumentado en las últimas 48 horas',
      'Se requiere optimización de palabras clave',
      'La audiencia está respondiendo bien a los nuevos creativos',
      'Se observa un incremento en el engagement',
      'Los costos por conversión están dentro del objetivo'
    ],
    note_added: [
      'Pendiente revisar métricas de rendimiento',
      'Cliente solicita reporte de resultados',
      'Programar reunión de seguimiento',
      'Actualizar creativos la próxima semana',
      'Revisar configuración de audiencias',
      'Analizar competencia en el sector',
      'Preparar propuesta de optimización'
    ]
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      setError('La descripción es requerida');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? PRODUCTION_CONFIG.API_BASE_URL 
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/campaign-tracking/${campaign.id}/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        // Show success message
        setShowSuccessMessage(true);
        
        // Wait a bit to show the success message, then close
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      } else {
        throw new Error('Error al agregar entrada');
      }
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateClick = (template) => {
    setFormData(prev => ({
      ...prev,
      description: template
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">⚡ Acción Rápida - Campaña</h2>
              <p className="text-purple-100 mt-1">
                {campaign.nombre_cliente} - {campaign.campana}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tipo de Acción */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Tipo de Acción
              </label>
              <div className="grid grid-cols-1 gap-3">
                {actionTypes.map((type) => (
                  <label
                    key={type.value}
                    className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                      formData.action_type === type.value
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="action_type"
                      value={type.value}
                      checked={formData.action_type === type.value}
                      onChange={(e) => setFormData(prev => ({ ...prev, action_type: e.target.value }))}
                      className="sr-only"
                    />
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${type.color} mr-3`}>
                      {type.label}
                    </span>
                    <div className="flex-1" />
                    {formData.action_type === type.value && (
                      <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Templates Rápidos */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Templates Rápidos
              </label>
              <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                {quickTemplates[formData.action_type]?.map((template, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleTemplateClick(template)}
                    className="text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 text-sm border border-gray-200 hover:border-gray-300"
                  >
                    {template}
                  </button>
                ))}
              </div>
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Descripción *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={
                  formData.action_type === 'observation_added' 
                    ? "Describe lo que has observado en la campaña..."
                    : "Escribe una nota sobre el seguimiento..."
                }
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                rows="4"
                required
              />
            </div>

            {/* Success Message */}
            {showSuccessMessage && (
              <div className="bg-green-50 border border-green-400 text-green-700 px-4 py-3 rounded-xl flex items-center animate-pulse">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">✅ Entrada agregada exitosamente al historial</span>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex">
                  <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Botones */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </div>
                ) : (
                  'Agregar al Historial'
                )}
              </button>
              
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CampaignTrackingQuickActionModal;
